<template>
  <div>
    <tableComponent
      ref="tableComponent"
      :tableSettings="tableSettings"
      :tableData="tableData"
      :headers="headers"
      @manageTemplates="goToTemplates"
      @openMultiUserDialog="openMultiUserDialog"
      @openForm="openForm"
      @newBetriebsanweisung="newBetriebsanweisung"
      @reloadData="reloadDataFunc"
      @openBulkMove="openBulkMove"
      @confirmBulkDelete="confirmBulkDelete"
      @openImport="openImport"
    />
    <betriebsanweisungImportDialog
      :importDialog="importDialog"
      :loading="importing"
      @triggerUpload="importData"
    />
    <moveDialog
      :item="bulkMove.item"
      :moveDialog="bulkMove.dialog"
      :loading="bulkMove.loading"
      :array="bulkMove.targets"
      @cancel="bulkMove.dialog.open = false"
      @confirm="handleBulkMoveConfirm"
    />
    <v-dialog v-model="bulkConfirm.open" max-width="440">
      <v-card>
        <v-card-title>{{ bulkConfirm.title }}</v-card-title>
        <v-card-text>{{ bulkConfirm.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="bulkConfirm.open = false"
            :disabled="bulkConfirm.loading"
          >
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            :loading="bulkConfirm.loading"
            @click="runBulkDelete"
          >
            {{ $t("operatingInstructions.bulk.deleteButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <operatingInstructionForm
      ref="operatingInstructionForm"
      :data="operatingInstructionForm"
      @reloadData="reloadDataFunc"
    />
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="operatingInstruction"
      :availableItems="transferItems"
      itemText="name"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.operatingInstructions"
    />
  </div>
</template>
<script>
import tableComponent from "../../components/_utils/dataTableHelpers/tableComponent.vue";
import operatingInstructionForm from "./operatingInstructionForm.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";
import moveDialog from "../../components/_utils/dataTableHelpers/actions/moveDialog.vue";
import betriebsanweisungImportDialog from "./betriebsanweisungImportDialog.vue";

export default {
  components: {
    tableComponent,
    operatingInstructionForm,
    multiUserDialog,
    moveDialog,
    betriebsanweisungImportDialog,
  },
  data: () => ({
    importDialog: { open: false },
    importing: false,
    currentUserId: "",
    operatingInstructionForm: {
      open: false,
      title: "operatingInstructions.upload",
      item: null,
      method: "new",
    },
    bulkMove: {
      item: {},
      loading: false,
      targets: [],
      selected: [],
      dialog: {
        open: false,
        title: "operatingInstructions.bulk.moveTitle",
        description: "",
      },
    },
    bulkConfirm: {
      open: false,
      loading: false,
      title: "",
      message: "",
      selected: [],
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
    transferItems: [],
    templateSourceIds: [],
  }),
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.currentUserId = user?._id || "";
    this.isMultiUser = user?.role === "MultiUser";

    const categoryId = this.$route.params.instructionCategoryId;
    const [uploaded, created] = await Promise.all([
      this.$store.dispatch("getOperatingInstructions", {
        instructionCategoryId: categoryId,
      }),
      this.$store.dispatch("getBetriebsanweisungen", {
        instructionCategoryId: categoryId,
      }),
    ]);

    const uploadedItems = ((uploaded && uploaded.data) || uploaded || []).map(
      (item) => ({ ...item, _transferType: "operatingInstruction" })
    );
    const createdItems = ((created && created.data) || created || []).map(
      (item) => ({
        ...item,
        name: item.title,
        _transferType: "betriebsanweisung",
      })
    );
    this.transferItems = [...uploadedItems, ...createdItems];

    await this.loadTemplateSourceIds();
  },
  computed: {
    categoryId() {
      return this.$route.params.instructionCategoryId;
    },
    tableSettings() {
      const buttons = [
        {
          title: "betriebsanweisung.manageTemplates",
          color: "primary",
          action: "manageTemplates",
          icon: "mdi-file-document-outline",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "multiUser.button",
          color: "primary",
          action: "openMultiUserDialog",
          icon: "mdi-swap-horizontal",
          iconDark: true,
          dark: true,
        },
        {
          title: "operatingInstructions.upload",
          color: "secondary",
          action: "openForm",
          icon: "cloud_upload",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "betriebsanweisung.import.button",
          color: "grey",
          action: "openImport",
          icon: "download",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "betriebsanweisung.new",
          color: "secondary",
          action: "newBetriebsanweisung",
          icon: "add",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ];
      return {
        title: "",
        showSelect: true,
        showHeaderToolbar: true,
        headerButtons: this.isMultiUser
          ? buttons
          : buttons.filter((b) => b.action !== "openMultiUserDialog"),
        multipleSelectButtons: [
          {
            title: "operatingInstructions.bulk.moveButton",
            color: "primary",
            action: "openBulkMove",
            icon: "content_paste_go",
            iconDark: true,
            dark: true,
            outlined: true,
          },
          {
            title: "operatingInstructions.bulk.deleteButton",
            color: "error",
            action: "confirmBulkDelete",
            icon: "delete",
            iconDark: true,
            dark: true,
            outlined: true,
          },
        ],
      };
    },
    headers() {
      return [
        {
          text: this.$t("operatingInstructions.tableHeader.description"),
          value: "displayTitle",
          align: "left",
        },
        {
          text: this.$t("operatingInstructions.tableHeader.kind"),
          value: "operatingInstructionKind",
          sortable: false,
        },
        {
          text: this.$t("operatingInstructions.tableHeader.tag"),
          value: "color",
        },
        {
          text: this.$t("riskAssessment.tableHeader.proofed"),
          value: "operatingInstructionProofStatus",
          sortable: false,
        },
        {
          text: "",
          value: "operatingInstructionAction",
          align: "right",
          sortable: false,
          width: 200,
        },
      ];
    },
    tableData() {
      return {
        tableComponentSlots: [
          {
            id: "oiColor",
            slotName: "item.color",
            componentName: "operationInstructionColor",
          },
          {
            id: "oiKind",
            slotName: "item.operatingInstructionKind",
            componentName: "operatingInstructionKind",
          },
          {
            id: "oiProof",
            slotName: "item.operatingInstructionProofStatus",
            componentName: "operatingInstructionProofStatus",
          },
          {
            id: "oiAction",
            slotName: "item.operatingInstructionAction",
            componentName: "operatingInstructionRowAction",
            config: {
              templateSourceIds: this.templateSourceIds,
              currentUserId: this.currentUserId,
            },
          },
        ],
        itemKey: "_id",
        uriPartPrepend: "operatingInstruction/combined",
        uriPartAppend: `&instructionCategory=${this.categoryId}`,
        populate: [],
        sort: "-createdAt",
        editFormPushName: "operatingInstructionForm",
      };
    },
  },
  methods: {
    async loadTemplateSourceIds() {
      const res = await this.$store.dispatch("getBetriebsanweisungTemplates");
      const templates = (res && res.data) || [];
      this.templateSourceIds = templates
        .filter((t) => t.sourceBetriebsanweisung)
        .map((t) => String(t.sourceBetriebsanweisung));
    },
    reloadDataFunc() {
      this.$refs.tableComponent.getData();
      this.loadTemplateSourceIds();
    },
    splitByKind(items) {
      const created = [];
      const uploads = [];
      (items || []).forEach((i) => {
        if (i._kind === "created") created.push(i._id);
        else uploads.push(i._id);
      });
      return { created, uploads };
    },
    async openBulkMove(selectedItems) {
      this.bulkMove.selected = selectedItems || [];
      const res = await this.$store.dispatch("getAllInstructionCategories");
      const cats = (res && res.data) || res || [];
      this.bulkMove.targets = cats
        .map((c) => ({ id: c.id || c._id, name: c.name }))
        .filter(
          (t) =>
            String(t.id) !== String(this.$route.params.instructionCategoryId)
        );
      this.bulkMove.dialog.description = this.$t(
        "operatingInstructions.bulk.moveText",
        { count: this.bulkMove.selected.length }
      );
      this.bulkMove.dialog.open = true;
    },
    async handleBulkMoveConfirm(targetId) {
      try {
        this.bulkMove.loading = true;
        const { created, uploads } = this.splitByKind(this.bulkMove.selected);
        const jobs = [];
        if (uploads.length) {
          jobs.push(
            this.$store.dispatch("bulkMoveOperatingInstructions", {
              ids: uploads,
              instructionCategory: targetId,
            })
          );
        }
        if (created.length) {
          jobs.push(
            this.$store.dispatch("bulkMoveBetriebsanweisungen", {
              ids: created,
              instructionCategory: targetId,
            })
          );
        }
        await Promise.all(jobs);
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t("operatingInstructions.bulk.movedMessage")
        );
        this.bulkMove.dialog.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkMove.loading = false;
      }
    },
    confirmBulkDelete(selectedItems) {
      const selected = selectedItems || [];
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("operatingInstructions.bulk.deleteTitle"),
        message: this.$t("operatingInstructions.bulk.deleteConfirm", {
          count: selected.length,
        }),
        selected,
      };
    },
    async runBulkDelete() {
      this.bulkConfirm.loading = true;
      try {
        const { created, uploads } = this.splitByKind(
          this.bulkConfirm.selected
        );
        const jobs = [];
        if (uploads.length) {
          jobs.push(
            this.$store.dispatch("bulkDeleteOperatingInstructions", uploads)
          );
        }
        if (created.length) {
          jobs.push(
            this.$store.dispatch("bulkDeleteBetriebsanweisungen", created)
          );
        }
        await Promise.all(jobs);
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t("operatingInstructions.bulk.deletedMessage")
        );
        this.bulkConfirm.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkConfirm.loading = false;
      }
    },
    onBulkActionDone() {
      if (this.$refs.tableComponent) {
        this.$refs.tableComponent.clearSelectItems();
      }
      this.reloadDataFunc();
    },
    openForm() {
      this.operatingInstructionForm.open = true;
    },
    goToTemplates() {
      this.$router.push({
        name: "betriebsanweisungTemplates",
        query: {
          targetId: this.$route.params.instructionCategoryId,
          targetName: this.$route.params.instructionCategoryName,
        },
      });
    },
    openImport() {
      this.importDialog.open = true;
    },
    async importData(file) {
      if (!file || !file.content) return;
      this.importing = true;
      try {
        await this.$store.dispatch("createBetriebsanweisungImport", {
          file: file.content,
          instructionCategory: this.categoryId,
        });
        this.importDialog.open = false;
        this.reloadDataFunc();
      } finally {
        this.importing = false;
      }
    },
    newBetriebsanweisung() {
      this.$router.push({
        name: "betriebsanweisungForm",
        params: {
          instructionCategoryId: this.categoryId,
          instructionCategoryName: this.$route.params.instructionCategoryName,
          id: "1",
          mode: "new",
        },
      });
    },
    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        return;
      }
      this.multiUserOpen = true;
    },
    async executeTransfer(payload) {
      try {
        const byType = { operatingInstruction: [], betriebsanweisung: [] };
        for (const id of payload.entityIds) {
          const item = this.transferItems.find(
            (entry) => (entry._id || entry.id) === id
          );
          const type = item?._transferType || "operatingInstruction";
          byType[type].push(id);
        }

        const totals = { totalSuccess: 0, totalConflict: 0, totalError: 0 };
        for (const [entityType, entityIds] of Object.entries(byType)) {
          if (!entityIds.length) continue;
          const result = await this.$store.dispatch("transferEntities", {
            ...payload,
            entityType,
            entityIds,
          });
          totals.totalSuccess += result.totalSuccess || 0;
          totals.totalConflict += result.totalConflict || 0;
          totals.totalError += result.totalError || 0;
        }

        const parts = [];
        if (totals.totalSuccess > 0)
          parts.push(
            this.$t("multiUser.result.success", { number: totals.totalSuccess })
          );
        if (totals.totalConflict > 0)
          parts.push(
            this.$t("multiUser.result.error", { number: totals.totalConflict })
          );
        if (totals.totalError > 0)
          parts.push(
            this.$t("multiUser.result.error", { number: totals.totalError })
          );
        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));
        if (payload.action === "move" && totals.totalSuccess > 0) {
          this.reloadDataFunc();
        }
      } catch (e) {
        console.error("Transfer failed:", e);
      }
    },
  },
};
</script>
